import { createSignal, untrack } from "solid-js";
import { parseHash, serializeHash, type HashState, type View } from "./hash-query";

// Single source of truth for the URL hash, exposed as a Solid signal.
// Components subscribe via useHashState(); writes go through setHashState().

const [hashState, setHashStateInternal] = createSignal<HashState>(
  parseHash(typeof window !== "undefined" ? window.location.hash : ""),
);

// On any URL change the listener compares the new hash to the in-memory
// signal and only updates the signal when they actually diverge — that
// way our own `history.replaceState` writes (which keep the URL and the
// signal in sync) are a no-op. We deliberately don't use a "suppress
// the next event" boolean: `replaceState` doesn't fire `hashchange` per
// spec, so the flag would never get cleared and any legitimate external
// hash change (back button, paste, devtools) would be silently dropped.
function onHashChange() {
  const fromUrl = parseHash(window.location.hash);
  if (sameHashState(fromUrl, untrack(hashState))) return;
  setHashStateInternal(fromUrl);
}

function sameHashState(a: HashState, b: HashState): boolean {
  if (a.view !== b.view) return false;
  const aKeys = Object.keys(a.params);
  const bKeys = Object.keys(b.params);
  if (aKeys.length !== bKeys.length) return false;
  for (const k of aKeys) {
    const av = a.params[k];
    const bv = b.params[k];
    if (!bv || av!.length !== bv.length) return false;
    for (let i = 0; i < av!.length; i++) {
      if (av![i] !== bv[i]) return false;
    }
  }
  return true;
}

if (typeof window !== "undefined") {
  window.addEventListener("hashchange", onHashChange);
}

export function useHashState() {
  return hashState;
}

// Writes the new state to the URL AND updates the signal in one step.
// `history.replaceState` doesn't fire `hashchange`, so there's nothing
// to suppress — the listener's hash-vs-signal compare keeps us idempotent
// on the rare cases where the browser does fire one (Safari quirks, etc.).
export function setHashState(next: HashState): void {
  const encoded = serializeHash(next);
  if (encoded === window.location.hash) return;
  window.history.replaceState(null, "", encoded);
  setHashStateInternal(next);
}

// Convenience: switch view, keep all params. Untracked read — callers should
// not auto-subscribe to hashState just because they invoked a setter.
export function setView(view: View): void {
  const current = untrack(hashState);
  setHashState({ view, params: current.params });
}

// Convenience: merge params for a single prefix (e.g., "c." or "v."),
// preserving everything else. The hashState() read is untracked so the
// calling reactive scope (typically a store→URL effect) does not subscribe
// to the signal it is about to write — that auto-subscription would create
// a self-trigger cycle on every URL change.
export function setPrefixedParams(
  prefix: string,
  next: Record<string, string[]>,
): void {
  const current = untrack(hashState);
  const merged: Record<string, string[]> = {};
  // Keep all keys NOT matching this prefix
  for (const [k, v] of Object.entries(current.params)) {
    if (!k.startsWith(prefix)) merged[k] = v;
  }
  // Add the new prefix-keyed values
  for (const [k, v] of Object.entries(next)) {
    if (v.length) merged[k] = v;
  }
  setHashState({ view: current.view, params: merged });
}

// Used by tests / hot reload.
export function _disposeRouter() {
  if (typeof window !== "undefined") {
    window.removeEventListener("hashchange", onHashChange);
  }
}
